import React, { useState, useRef, useEffect } from 'react';
import {
  Send, Loader2, CheckCircle2, Clock, AlertTriangle,
  ChevronRight, FileText, BarChart2, Edit2, Check, X, GitBranch
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MD_TABLE_COMPONENTS } from '../../Common/MarkdownContent';
import api from '../../../api';
import toast from 'react-hot-toast';
import { Message, Manifest, ManifestVariable, ManifestTeam, InterTeamDependency, ProjectSetup } from '../datasim.types';

// ─── Phase indicator data ──────────────────────────────────────────────────────

const PHASES = [
  { id: 'phase_0', label: 'Team Map', short: '0' },
  { id: 'phase_1', label: 'Orientation', short: '1' },
  { id: 'phase_2', label: 'Variables', short: '2' },
  { id: 'phase_3', label: 'Constraints', short: '3' },
  { id: 'phase_4', label: 'Audit', short: '4' },
  { id: 'complete', label: 'Complete', short: '✓' },
];

function getPhaseIndex(phase?: string): number {
  const idx = PHASES.findIndex(p => p.id === phase);
  return idx === -1 ? 1 : idx;
}

// ─── Variable confidence colors ────────────────────────────────────────────────

function confidenceColor(confidence?: string) {
  if (confidence === 'high') return { dot: 'bg-green-500', badge: 'bg-green-50 text-green-700 border-green-200' };
  if (confidence === 'low') return { dot: 'bg-red-400', badge: 'bg-red-50 text-red-700 border-red-200' };
  return { dot: 'bg-amber-400', badge: 'bg-amber-50 text-amber-700 border-amber-200' };
}

// ─── Inline variable editor ────────────────────────────────────────────────────

interface VarEditorProps {
  variable: ManifestVariable;
  unit: string;
  onSave: (updated: ManifestVariable) => void;
  onCancel: () => void;
}

function VarEditor({ variable, unit, onSave, onCancel }: VarEditorProps) {
  const p = variable.distribution?.params || {};
  const [opt, setOpt] = useState(String(p.optimistic ?? ''));
  const [ml, setMl] = useState(String(p.most_likely ?? ''));
  const [pess, setPess] = useState(String(p.pessimistic ?? ''));

  function handleSave() {
    const updated: ManifestVariable = {
      ...variable,
      distribution: {
        ...variable.distribution,
        type: variable.distribution?.type || 'pert',
        params: {
          optimistic: parseFloat(opt) || undefined,
          most_likely: parseFloat(ml) || undefined,
          pessimistic: parseFloat(pess) || undefined,
        },
      },
    };
    onSave(updated);
  }

  return (
    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
      <p className="text-xs font-semibold text-blue-700">Edit estimates ({unit})</p>
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: 'Optimistic', val: opt, set: setOpt },
          { label: 'Most Likely', val: ml, set: setMl },
          { label: 'Pessimistic', val: pess, set: setPess },
        ].map(f => (
          <div key={f.label}>
            <p className="text-xs text-gray-500 mb-1">{f.label}</p>
            <input
              type="number"
              value={f.val}
              onChange={e => f.set(e.target.value)}
              className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-blue-400"
            />
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <button onClick={handleSave} className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded text-xs font-medium">
          <Check className="w-3 h-3" /> Save
        </button>
        <button onClick={onCancel} className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium">
          <X className="w-3 h-3" /> Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Team Map (SVG diagram) ────────────────────────────────────────────────────

const TEAM_TYPE_COLORS: Record<string, { fill: string; stroke: string; text: string }> = {
  internal:         { fill: '#dbeafe', stroke: '#3b82f6', text: '#1e40af' },
  vendor:           { fill: '#fed7aa', stroke: '#f97316', text: '#c2410c' },
  external_partner: { fill: '#ede9fe', stroke: '#8b5cf6', text: '#6d28d9' },
  regulatory_body:  { fill: '#fee2e2', stroke: '#ef4444', text: '#991b1b' },
  mixed:            { fill: '#f3f4f6', stroke: '#9ca3af', text: '#374151' },
};

const RISK_COLORS: Record<string, string> = {
  low: '#22c55e', medium: '#f59e0b', high: '#ef4444',
};

function TeamMap({ manifest }: { manifest: Manifest }) {
  const rawTeams = manifest.teams || [];
  const deps = (manifest.program_topology?.inter_team_dependencies || []) as InterTeamDependency[];

  // Auto-derive missing teams from variables and dependencies
  const knownIds = new Set(rawTeams.map(t => t.id));
  const derivedTeams: ManifestTeam[] = [];

  // Check variables for team_ids not in teams array
  (manifest.variables || []).forEach(v => {
    if (v.team_id && !knownIds.has(v.team_id)) {
      knownIds.add(v.team_id);
      derivedTeams.push({
        id: v.team_id,
        name: v.team_id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        type: 'internal',
      });
    }
  });

  // Check dependencies for team_ids not yet known
  deps.forEach(d => {
    [d.from_team, d.to_team].forEach(tid => {
      if (tid && !knownIds.has(tid)) {
        knownIds.add(tid);
        derivedTeams.push({
          id: tid,
          name: tid.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          type: 'internal',
        });
      }
    });
  });

  const teams = [...rawTeams, ...derivedTeams];

  const BOX_W = 130, BOX_H = 52;
  const COL_GAP = 175, ROW_GAP = 92;
  const PAD = 24;
  const COLS = teams.length <= 2 ? teams.length : Math.min(3, Math.ceil(Math.sqrt(teams.length)));

  const pos: Record<string, { x: number; y: number }> = {};
  teams.forEach((t, i) => {
    const col = i % COLS;
    const row = Math.floor(i / COLS);
    pos[t.id] = { x: PAD + col * COL_GAP, y: PAD + row * ROW_GAP };
  });

  const rows = Math.ceil(teams.length / COLS);
  const svgW = PAD * 2 + (COLS - 1) * COL_GAP + BOX_W;
  const svgH = PAD * 2 + (rows - 1) * ROW_GAP + BOX_H;

  if (teams.length === 0) {
    return (
      <div className="p-8 text-center text-sm text-gray-400">
        Teams will appear here once the analyst captures them.
      </div>
    );
  }

  return (
    <div className="overflow-auto">
      <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`} className="max-w-full">
        <defs>
          {['low', 'medium', 'high'].map(risk => (
            <marker key={risk} id={`dsarrow-${risk}`} markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
              <path d="M0,0 L0,6 L8,3 z" fill={RISK_COLORS[risk]} />
            </marker>
          ))}
        </defs>

        {/* Dependency arrows */}
        {deps.map((dep, i) => {
          const from = pos[dep.from_team];
          const to = pos[dep.to_team];
          if (!from || !to || dep.from_team === dep.to_team) return null;
          const color = RISK_COLORS[dep.handoff_risk] || RISK_COLORS.medium;
          const x1 = from.x + BOX_W, y1 = from.y + BOX_H / 2;
          const x2 = to.x, y2 = to.y + BOX_H / 2;
          const mx = (x1 + x2) / 2;
          return (
            <g key={i}>
              <path
                d={`M ${x1} ${y1} C ${mx} ${y1} ${mx} ${y2} ${x2} ${y2}`}
                fill="none" stroke={color} strokeWidth="1.5" opacity="0.85"
                markerEnd={`url(#dsarrow-${dep.handoff_risk})`}
              />
              <text x={mx} y={(y1 + y2) / 2 - 4} textAnchor="middle" fontSize="8" fill={color} fontWeight="600">
                {dep.handoff_risk}
              </text>
            </g>
          );
        })}

        {/* Team boxes */}
        {teams.map((team: ManifestTeam) => {
          const p = pos[team.id];
          if (!p) return null;
          const colors = TEAM_TYPE_COLORS[team.type] || TEAM_TYPE_COLORS.mixed;
          const shortName = team.name.length > 17 ? team.name.slice(0, 15) + '…' : team.name;
          const subParts = [
            team.size != null ? `${team.size} ppl` : null,
            team.ai_readiness_tier != null ? `Tier ${team.ai_readiness_tier}` : null,
          ].filter(Boolean);
          return (
            <g key={team.id} transform={`translate(${p.x}, ${p.y})`}>
              <rect width={BOX_W} height={BOX_H} rx="7" fill={colors.fill} stroke={colors.stroke} strokeWidth="1.5" />
              <text x={BOX_W / 2} y={BOX_H / 2 - 6} textAnchor="middle" fontSize="11" fontWeight="700" fill={colors.text}>
                {shortName}
              </text>
              <text x={BOX_W / 2} y={BOX_H / 2 + 8} textAnchor="middle" fontSize="9" fill={colors.text} opacity="0.7">
                {team.type.replace(/_/g, ' ')}
              </text>
              {subParts.length > 0 && (
                <text x={BOX_W / 2} y={BOX_H - 5} textAnchor="middle" fontSize="8" fill={colors.text} opacity="0.55">
                  {subParts.join(' · ')}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 px-1">
        {Object.entries(TEAM_TYPE_COLORS).map(([type, c]) => (
          <div key={type} className="flex items-center gap-1">
            <div className="w-3 h-3 rounded border" style={{ backgroundColor: c.fill, borderColor: c.stroke }} />
            <span className="text-xs text-gray-500 capitalize">{type.replace(/_/g, ' ')}</span>
          </div>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 px-1">
        {Object.entries(RISK_COLORS).map(([risk, color]) => (
          <div key={risk} className="flex items-center gap-1">
            <div className="w-6 h-0.5 rounded" style={{ backgroundColor: color }} />
            <span className="text-xs text-gray-500 capitalize">{risk} handoff risk</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

interface Props {
  setup: ProjectSetup;
  messages: Message[];
  manifest: Manifest | null;
  onMessagesUpdate: (messages: Message[]) => void;
  onManifestUpdate: (manifest: Manifest) => void;
  onProceed: () => void;
}

// Status payload echoed back by /api/datasim/intake. All fields optional —
// the backend may omit them for old sessions or partial responses, and the UI
// degrades gracefully (indicator just doesn't render).
interface IntakeMeta {
  input_tokens?: number;
  input_tokens_source?: 'api' | 'estimate';
  output_tokens?: number;
  context_usage_pct?: number | null;
  max_context_tokens?: number;
  compression_triggered?: boolean;
  compression_reason?: string | null;
  brief_generated?: boolean;
  window_size?: number;
  turn_count?: number;
  variables_captured?: number;
  variables_pending?: number;
}

export default function DataSimIntake({
  setup, messages, manifest, onMessagesUpdate, onManifestUpdate, onProceed
}: Props) {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingVarId, setEditingVarId] = useState<string | null>(null);
  const [rightTab, setRightTab] = useState<'manifest' | 'team-map'>('manifest');
  // contextMeta tracks the most recent /intake response status payload —
  // used both to drive the "Context: X%" indicator and to send last_input_tokens
  // back on the next call so the backend can use the real token count as its
  // primary compression signal.
  const [contextMeta, setContextMeta] = useState<IntakeMeta | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (!hasStarted.current && messages.length === 0 && !manifest) {
      hasStarted.current = true;
      startInterview();
    }
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  async function startInterview() {
    setLoading(true);
    try {
      const res = await api.post('/api/datasim/intake', {
        messages: [],
        manifest: {
          project_name: setup.projectName,
          project_type: setup.projectType,
          primary_output_metric: {
            type: setup.metricType,
            unit: setup.metricUnit,
            label: setup.metricLabel,
          },
        },
        uploaded_doc_text: setup.uploadedDocText || undefined,
      });
      onMessagesUpdate([{ role: 'assistant', content: res.data.response }]);
      if (res.data.manifest) onManifestUpdate(res.data.manifest);
      if (res.data.meta) setContextMeta(res.data.meta as IntakeMeta);
    } catch {
      toast.error('Failed to start intake interview');
    } finally {
      setLoading(false);
    }
  }

  async function sendMessage() {
    const text = input.trim();
    if (!text || loading) return;
    const previousMessages = messages;
    const newMessages: Message[] = [...messages, { role: 'user', content: text }];
    onMessagesUpdate(newMessages);
    setInput('');
    setLoading(true);
    try {
      const res = await api.post('/api/datasim/intake', {
        messages: newMessages,
        manifest: manifest || null,
        // Send the API's actual input_tokens from last call back — the backend
        // uses this as its primary compression signal (vs the chars/4 heuristic).
        last_input_tokens: contextMeta?.input_tokens,
      }, { timeout: 240000 });
      onMessagesUpdate([...newMessages, { role: 'assistant', content: res.data.response }]);
      if (res.data.manifest) onManifestUpdate(res.data.manifest);
      if (res.data.meta) setContextMeta(res.data.meta as IntakeMeta);
    } catch (err: any) {
      // Roll back the user message so session remains in a valid state
      onMessagesUpdate(previousMessages);
      setInput(text);
      const isTimeout = err?.code === 'ECONNABORTED' || err?.message?.includes('timeout');
      toast.error(isTimeout
        ? 'Response is taking too long — please try again.'
        : 'Failed to send message — your message has been restored. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function handleVarSave(updated: ManifestVariable) {
    if (!manifest) return;
    const updatedVars = (manifest.variables || []).map(v => v.id === updated.id ? updated : v);
    onManifestUpdate({ ...manifest, variables: updatedVars });
    setEditingVarId(null);
  }

  const interviewPhase = manifest?.interview_phase;
  const currentPhaseIdx = getPhaseIndex(interviewPhase);
  const isMultiTeam = (manifest?.teams?.length ?? 0) > 0;

  const completeVars = (manifest?.variables || []).filter(
    v => v.distribution?.params?.optimistic != null &&
         v.distribution?.params?.pessimistic != null &&
         v.distribution?.params?.most_likely != null
  );

  const isReadyToProceed =
    manifest?.completeness_audit?.required_complete === true ||
    (completeVars.length >= 3 && interviewPhase === 'complete');

  const coverageScore = manifest?.completeness_audit?.coverage_score ?? 0;
  const unit = manifest?.primary_output_metric?.unit || setup.metricUnit;

  const visiblePhases = isMultiTeam ? PHASES : PHASES.filter(p => p.id !== 'phase_0');

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

      {/* ── LEFT: Chat ─────────────────────────────────────────────────────── */}
      <div className="flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden h-[680px]">

        {/* Phase progress bar + subtle context indicator (right side) */}
        <div className="px-4 pt-3 pb-2 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 flex-1 min-w-0">
              {visiblePhases.map((phase, idx) => {
                const done = currentPhaseIdx > (isMultiTeam ? idx : PHASES.findIndex(p => p.id === phase.id));
                const active = interviewPhase === phase.id;
                return (
                  <React.Fragment key={phase.id}>
                    <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold transition-all ${
                      active ? 'bg-red-600 text-white' :
                      done ? 'bg-green-100 text-green-700' :
                      'bg-gray-100 text-gray-400'
                    }`}>
                      {done ? <CheckCircle2 className="w-3 h-3" /> : null}
                      <span>{phase.label}</span>
                    </div>
                    {idx < visiblePhases.length - 1 && (
                      <div className={`flex-1 h-px ${done ? 'bg-green-300' : 'bg-gray-200'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            {/* Context usage indicator — only shown once the backend has
                reported a real input_tokens count. Color shifts green → amber
                → red as we approach the compression threshold so analysts
                see compression coming instead of wondering why the AI just
                "forgot" something. */}
            {typeof contextMeta?.context_usage_pct === 'number' && (
              <div
                title={
                  `Context tokens: ${contextMeta.input_tokens?.toLocaleString() ?? '—'}` +
                  (contextMeta.max_context_tokens
                    ? ` / ${contextMeta.max_context_tokens.toLocaleString()}`
                    : '') +
                  (contextMeta.input_tokens_source === 'estimate' ? ' (estimated)' : '') +
                  (contextMeta.compression_triggered
                    ? ` · compressed (${contextMeta.compression_reason || 'auto'})`
                    : '')
                }
                className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold tabular-nums flex-shrink-0 border ${
                  contextMeta.context_usage_pct >= 65
                    ? 'bg-red-50 text-red-700 border-red-200'
                    : contextMeta.context_usage_pct >= 40
                      ? 'bg-amber-50 text-amber-700 border-amber-200'
                      : 'bg-green-50 text-green-700 border-green-200'
                }`}
              >
                <span className="opacity-70">Context</span>
                <span>{contextMeta.context_usage_pct}%</span>
                {contextMeta.compression_triggered && (
                  <span className="opacity-70" aria-label="compressed">⤓</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Chat messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 && loading && (
            <div className="flex justify-start">
              <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 flex items-center gap-2">
                <Loader2 className="w-4 h-4 text-red-500 animate-spin" />
                <span className="text-sm text-gray-500">Analyst is preparing the interview...</span>
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[88%] rounded-xl px-4 py-2.5 text-sm leading-relaxed ${
                m.role === 'user'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-50 border border-gray-200 text-gray-800'
              }`}>
                {m.role === 'assistant' ? (
                  <div className="prose prose-sm max-w-none text-gray-800 [&_strong]:font-semibold [&_ul]:mt-1 [&_li]:mt-0.5">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_TABLE_COMPONENTS}>{m.content}</ReactMarkdown>
                  </div>
                ) : m.content}
              </div>
            </div>
          ))}
          {loading && messages.length > 0 && (
            <div className="flex justify-start">
              <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 flex items-center gap-2">
                <Loader2 className="w-4 h-4 text-red-500 animate-spin" />
                <span className="text-sm text-gray-500">
                  {typeof contextMeta?.context_usage_pct === 'number' && contextMeta.context_usage_pct >= 65
                    ? 'Analyst is thinking (condensing earlier notes to stay sharp)…'
                    : 'Analyst is thinking...'}
                </span>
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Input area */}
        <div className="border-t border-gray-100 p-3 space-y-2">
          {isReadyToProceed && (
            <button
              onClick={onProceed}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 transition-colors"
            >
              <CheckCircle2 className="w-4 h-4" />
              Intake Complete — Review Constraints
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
          <div className="flex gap-2">
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              placeholder="Respond to the analyst..."
              rows={2}
              disabled={loading}
              className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200 disabled:opacity-50"
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || loading}
              className="px-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-40"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* ── RIGHT: Live Manifest / Team Map ──────────────────────────────── */}
      <div className="space-y-4 overflow-y-auto max-h-[680px] pr-1">

        {/* Tab switcher (only when teams exist) */}
        {isMultiTeam && (
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setRightTab('manifest')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                rightTab === 'manifest' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileText className="w-3.5 h-3.5" /> Manifest
            </button>
            <button
              onClick={() => setRightTab('team-map')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                rightTab === 'team-map' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <GitBranch className="w-3.5 h-3.5" /> Team Map
            </button>
          </div>
        )}

        {/* Team Map panel */}
        {isMultiTeam && rightTab === 'team-map' && manifest && (
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <GitBranch className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-bold text-gray-700">Team Dependency Map</span>
              <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                {manifest.teams?.length ?? 0} teams
              </span>
            </div>
            <TeamMap manifest={manifest} />
          </div>
        )}

        {/* Manifest content (hidden when team-map tab active) */}
        {rightTab === 'manifest' && (
          <>

        {/* Project info header */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Project</p>
              <p className="text-sm font-bold text-gray-800 mt-0.5">{setup.projectName}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400">{setup.projectType.replace('_', ' ')}</p>
              <p className="text-xs font-semibold text-gray-600 mt-0.5">{setup.metricLabel} · {unit}</p>
            </div>
          </div>
        </div>

        {/* Variables manifest */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-slate-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-bold text-gray-700">Variable Manifest</span>
              {completeVars.length > 0 && (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                  {completeVars.length} ready
                </span>
              )}
            </div>
            {(manifest?.variables?.length ?? 0) > 0 && (
              <span className="text-xs text-gray-400">{manifest!.variables!.length} captured</span>
            )}
          </div>

          {!(manifest?.variables?.length) ? (
            <div className="p-8 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gray-50 flex items-center justify-center">
                <BarChart2 className="w-6 h-6 text-gray-200" />
              </div>
              <p className="text-sm text-gray-400">Variables will appear here as the analyst captures them</p>
              <p className="text-xs text-gray-300 mt-1">Each response will update this panel</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {manifest!.variables!.map(v => {
                const p = v.distribution?.params;
                const complete = p?.optimistic != null && p?.most_likely != null && p?.pessimistic != null;
                const colors = confidenceColor(v.confidence);
                const isEditing = editingVarId === v.id;
                return (
                  <div key={v.id} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 min-w-0">
                        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${colors.dot}`} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{v.name}</p>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            <span className="text-xs text-gray-400">{v.type}</span>
                            {v.unit && <span className="text-xs text-gray-400">· {v.unit}</span>}
                            {v.confidence && (
                              <span className={`text-xs px-1.5 py-0.5 rounded border font-medium ${colors.badge}`}>
                                {v.confidence}
                              </span>
                            )}
                            {(v.applied_constraints || []).map(c => (
                              <span key={c} className="text-xs bg-purple-50 text-purple-600 border border-purple-200 px-1.5 py-0.5 rounded">
                                {c}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {complete ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        ) : (
                          <Clock className="w-4 h-4 text-amber-400" />
                        )}
                        <button
                          onClick={() => setEditingVarId(isEditing ? null : v.id)}
                          className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {complete && !isEditing && (
                      <div className="mt-1.5 ml-4 grid grid-cols-3 gap-1">
                        {[
                          { l: 'opt', v: p!.optimistic },
                          { l: 'ml', v: p!.most_likely },
                          { l: 'pess', v: p!.pessimistic },
                        ].map(s => (
                          <div key={s.l} className="bg-gray-50 rounded px-2 py-1 text-center">
                            <p className="text-xs text-gray-400">{s.l}</p>
                            <p className="text-xs font-bold text-gray-700">{s.v}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {isEditing && (
                      <VarEditor
                        variable={v}
                        unit={unit}
                        onSave={handleVarSave}
                        onCancel={() => setEditingVarId(null)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Coverage bar */}
          {(coverageScore > 0 || (manifest?.completeness_audit?.open_gaps?.length ?? 0) > 0) && (
            <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
              <div className="flex justify-between mb-1.5">
                <span className="text-xs font-medium text-gray-600">Completeness</span>
                <span className="text-xs font-bold text-gray-700">{Math.round(coverageScore * 100)}%</span>
              </div>
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${Math.round(coverageScore * 100)}%`,
                    backgroundColor: coverageScore >= 0.8 ? '#22c55e' : coverageScore >= 0.5 ? '#f59e0b' : '#ef4444'
                  }}
                />
              </div>
              {(manifest?.completeness_audit?.open_gaps?.length ?? 0) > 0 && (
                <div className="mt-2 space-y-1">
                  {(manifest!.completeness_audit!.open_gaps ?? []).slice(0, 3).map((gap, i) => (
                    <div key={i} className="flex items-start gap-1.5">
                      <AlertTriangle className="w-3 h-3 text-amber-400 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-500">{gap}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* External dependencies */}
        {(manifest?.external_dependencies?.length ?? 0) > 0 && (
          <div className="bg-white border border-orange-200 rounded-xl overflow-hidden shadow-sm">
            <div className="px-4 py-3 border-b border-orange-100 bg-orange-50">
              <p className="text-sm font-bold text-orange-800">External Dependencies</p>
              <p className="text-xs text-orange-600 mt-0.5">These will be modeled as volatility events</p>
            </div>
            <div className="divide-y divide-orange-50">
              {manifest!.external_dependencies!.map(dep => (
                <div key={dep.id} className="px-4 py-2.5 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{dep.name}</p>
                    {dep.owner && <p className="text-xs text-gray-400">{dep.owner}</p>}
                  </div>
                  {dep.probability_on_time != null && (
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      dep.probability_on_time >= 0.8 ? 'bg-green-100 text-green-700' :
                      dep.probability_on_time >= 0.5 ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {Math.round(dep.probability_on_time * 100)}% on time
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Skip button */}
        {completeVars.length >= 2 && !isReadyToProceed && (
          <button
            onClick={onProceed}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-500 hover:bg-gray-50 transition-colors"
          >
            Skip to Constraint Review ({completeVars.length} variables captured)
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
          </>
        )}
      </div>
    </div>
  );
}
